import type { SupFormImplementationId } from "@/lib/swr";
import type {
  Analytics,
  ChainType,
  ProtocolMetadata,
  SupSingleVaultV3,
  SupVaultReward,
  SupWeb3Address,
  SupWeb3Asset,
  SupWeb3TokenSymbol,
  VaultAudit,
  vaultAvailability,
  VaultExploit,
  VaultFaq,
  VaultFees,
  VaultKyc,
  VaultOther,
  vaultVisibility,
} from "@/types";
import dictionary from "@/../config/dictionary.config";

export interface ResponseVaults {
  analytics: Analytics[] | null;
  assets: SupWeb3Asset[] | null;
  audits: VaultAudit[] | null;
  availability: vaultAvailability | "";
  chain: ChainType;
  contract_address: SupWeb3Address;
  created_at: string;
  decimals: number;
  defillama_id_id: string;
  deploy_date: string;
  description: string;
  exploits: VaultExploit[] | null;
  external_url: string;
  faq: VaultFaq[] | null;
  fees: VaultFees[] | null;
  form_beacon_id: number | null; // @todo: use address
  friendly_name: string;
  id: string;
  is_authenticated: boolean;
  is_supervault: boolean;
  kyc: VaultKyc[] | null;
  name: string;
  num_depositors: number; // @todo: use address or user (?)
  other: VaultOther[] | null;
  protocol: ProtocolMetadata;
  protocol_id: string;
  superform_address: SupWeb3Address | null;
  has_rewards: boolean;
  superform_id: string;
  has_aerc_20_registered: boolean;
  has_timelock: boolean;
  rewards: SupVaultReward[] | null;
  symbol: SupWeb3TokenSymbol;
  recommend_vault_share_withdrawal: boolean;
  withdrawal_link?: string;
  form_implementation_id: SupFormImplementationId;

  super_pools: number[];

  updated_at: string;
  vault_statistics: {
    vault_id: string;
    superform_id: number;
    apy_now: number;
    tvl_now: number;
    sharpe_day: number;
    sharpe_month: number;
    apy_day: number;
    apy_week: number;
    apy_month: number;
    tvl_day_change: number;
    tvl_week_change: number;
    tvl_month_change: number;
    pps: number;
    pps_usd: number;
    displayApy: number;
  };
  vault_lister: string;
  vault_status_message: string;
  visibility: vaultVisibility | "";
  yield_type: string;
  timelock?: boolean;
  timelock_duration: number;
  timelock_unit: string;
}

/**
 * Returns the most actual stat from the list of stats
 * Used for TVL, APY and Share stats
 *
 * @todo: wrap this calculations into some helper function or class to avoid custom props
 *
 * @param {number | null} hourlyStat
 * @param {number | null} dailyStat
 * @param {number | null} weeklyStat
 * @param {number | null} monthlyStat
 * @returns {number}
 */
export function getActualStat(hourlyStat: number | null, dailyStat: number | null, weeklyStat: number | null, monthlyStat: number | null): number {
  if (monthlyStat !== null && monthlyStat !== dictionary.statsDefaultValue) {
    return monthlyStat;
  }
  if (weeklyStat !== null && weeklyStat !== dictionary.statsDefaultValue) {
    return weeklyStat;
  }
  if (dailyStat !== null && dailyStat !== dictionary.statsDefaultValue) {
    return dailyStat;
  }
  if (hourlyStat !== null && hourlyStat !== dictionary.statsDefaultValue) {
    return hourlyStat;
  }
  return dictionary.statsDefaultValue;
}

/**
 * Processing & filtering vault data after fetching data from API
 *
 * @param {ResponseVaults} singleVault
 * @return {SupSingleVaultV3}
 */
export function processVault(singleVault: ResponseVaults): SupSingleVaultV3 {
  const hasSuperformAddress = Boolean(singleVault.superform_address);
  return {
    ...singleVault,
    assets: singleVault.assets ?? [],
    friendly_name: singleVault.friendly_name || singleVault.name,
    vault_statistics: {
      ...singleVault.vault_statistics,
      displayApy: getActualStat(
        singleVault.vault_statistics.apy_now,
        singleVault.vault_statistics.apy_day,
        singleVault.vault_statistics.apy_week,
        singleVault.vault_statistics.apy_month
      ),
      displayTvl: getActualStat(
        singleVault.vault_statistics.tvl_now,
        singleVault.vault_statistics.tvl_now,
        singleVault.vault_statistics.tvl_now,
        singleVault.vault_statistics.tvl_now
      ),
      displaySharpe: getActualStat(
        singleVault.vault_statistics.sharpe_day,
        singleVault.vault_statistics.sharpe_day,
        singleVault.vault_statistics.sharpe_month,
        singleVault.vault_statistics.sharpe_month
      ),
    },
    exploits: singleVault.exploits ?? [],
    faq: singleVault.faq ?? [],
    fees: singleVault.fees ?? [],
    kyc: singleVault.kyc ?? [],
    other: singleVault.other ?? [],
    analytics: singleVault.analytics ?? [],
    audits: singleVault.audits ?? [],
    visibility: singleVault.visibility !== "" ? singleVault.visibility : hasSuperformAddress ? "listed" : "unlisted",
    availability: singleVault.availability !== "" ? singleVault.availability : hasSuperformAddress ? "available" : "unavailable",
    description: singleVault.description || "",
    yield_type: singleVault.yield_type || "",

    slug: String(singleVault.id),
    protocol: singleVault.protocol,
    image: !singleVault.assets ? "" : singleVault.assets[0].image,
  };
}

export function isAuthenticated(vault: SupSingleVaultV3) {
  return (vault.is_authenticated || vault.protocol?.is_authenticated) ?? false;
}
